import {
  defineMessages,
  FormattedMessage,
  MessageDescriptor,
  useIntl,
} from 'react-intl';
import {ReactNode, useCallback, useEffect, useRef, useState} from 'react';

import {Action, css, Icon, Tooltip, useResizeObserver, view} from '@sail/ui';
import {add, history as historyIcon} from '@sail/icons/react/Icon';
import {animate, view as animatedView, AnimatePresence} from '@sail/engine';

import {
  AssistantDrawerHeader,
  AssistantWidgetHeader,
  ConversationDevBadge,
  useAnalytics,
  useTheme,
} from '@stripe-internal/conversations-ui';
import {Conversation} from '@stripe-internal/conversations-client';
import {ViewMode} from '@stripe-internal/support-conversations-controller';

import {HeaderActionsContent} from '../../../StripeAssistant/components/Header/HeaderActionsContent';
import {sendMinimizedHeight} from '../../../core/lib/parentMessageBus/messages';
import {ThreadsActiveTopBar} from './ThreadsActiveTopBar';
import {
  useThreadsApp,
  useThreadsController,
  useThreadsConversationViewModelState,
  useThreadsCurrentConversationTitle,
  useThreadsEntryPoint,
  useThreadsFlags,
  useThreadsHistoryViewModel,
  useThreadsParentMessageBus,
  useThreadsViewMode,
  useThreadsVisibility,
  useThreadsWithHistory,
} from '../../app/hooks';

const titleMessages = defineMessages({
  newAssistantChat: {
    id: 'conversations_assistant.threads.header.newAssistantChat',
    description:
      'Default assistant header title when conversation history is enabled',
    defaultMessage: 'New assistant chat',
  },
});

const historyMessages = defineMessages({
  newConversation: {
    id: 'conversations_assistant.threads.header.newConversation',
    description: 'Tooltip for starting a new conversation',
    defaultMessage: 'New chat',
  },
  conversationHistory: {
    id: 'conversations_assistant.threads.header.conversationHistory',
    description: 'Tooltip for opening recent conversation history',
    defaultMessage: 'Recent chats',
  },
});

const buttonStyles = css({
  display: 'inline-flex',
  borderRadius: 'rounded',
  padding: 'small',
  ':hover': {
    backgroundColor: 'offset',
  },
});

type Props = {
  conversation?: Conversation | null;
  title?: MessageDescriptor | string;
  height?: number | string;
  badge?: ReactNode;
  headerActions?: ReactNode;
  subHeader?: ReactNode;
};

const TITLE_TRANSITION_DURATION_MS = 300;

function AnimatedTitle({
  title,
}: {
  title: MessageDescriptor | string | undefined;
}) {
  const intl = useIntl();
  const theme = useTheme();
  const conversationTitle = useThreadsCurrentConversationTitle();
  const {enableConversationsThreads, enableConversationsThreadsHistory} =
    useThreadsFlags();

  const resolveDefault = (): string => {
    if (title) {
      return typeof title === 'string' ? title : intl.formatMessage(title);
    }
    if (enableConversationsThreads && enableConversationsThreadsHistory) {
      return intl.formatMessage(titleMessages.newAssistantChat);
    }
    return theme.assistantHeaderTitle ?? '';
  };

  const [currentTitle, setCurrentTitle] = useState(
    conversationTitle ?? resolveDefault(),
  );
  const [isVisible, setIsVisible] = useState(true);
  const [isOverflowing, setIsOverflowing] = useState<boolean | null>(null);
  const titleWrapperRef = useRef<HTMLSpanElement | null>(null);
  const prevConversationTitleRef = useRef<string | undefined>(
    conversationTitle,
  );
  const hasAnimatedRef = useRef(false);

  const measureOverflowIfNeeded = useCallback(() => {
    if (isOverflowing !== null) {
      return;
    }

    const titleElement = titleWrapperRef.current?.firstElementChild;
    if (titleElement instanceof HTMLElement) {
      setIsOverflowing(titleElement.scrollWidth > titleElement.clientWidth);
    }
  }, [isOverflowing]);

  useEffect(() => {
    setIsOverflowing(null);
  }, [currentTitle]);

  useResizeObserver({
    node: titleWrapperRef,
    handler: () => {
      setIsOverflowing(null);
    },
    avoidInitialEvent: true,
  });

  useEffect(() => {
    const prevTitle = prevConversationTitleRef.current;
    prevConversationTitleRef.current = conversationTitle;

    if (!conversationTitle || hasAnimatedRef.current) {
      return;
    }

    // Only animate when the title first appears (transition from no title to a title).
    if (prevTitle) {
      return;
    }

    hasAnimatedRef.current = true;

    let exitTimeout: ReturnType<typeof setTimeout>;
    let enterTimeout: ReturnType<typeof setTimeout>;

    const startTimeout = setTimeout(() => {
      setIsVisible(false);
      exitTimeout = setTimeout(() => {
        setCurrentTitle(conversationTitle);
        enterTimeout = setTimeout(() => {
          setIsVisible(true);
        }, 50);
      }, TITLE_TRANSITION_DURATION_MS);
    }, 0);

    return () => {
      clearTimeout(startTimeout);
      clearTimeout(exitTimeout);
      clearTimeout(enterTimeout);
    };
  }, [conversationTitle]);

  // AnimatePresence must stay mounted at all times. If it were toggled on/off,
  // it would play animate.in on the old title (animPhase starts as null), setting
  // opacity to 0 immediately so the exit animation runs from invisible.
  // Always-mounted means animPhase is already 'in' when the exit triggers.
  // animate.in is omitted on first mount so the initial title appears instantly.
  return (
    <Tooltip
      type="label"
      placement="bottom start"
      trigger={
        // view.span gives the Tooltip a stable DOM node. overflow:visible lets
        // the exit animation translate upward without being clipped; the
        // animatedView.span handles its own text truncation via overflow:hidden.
        <view.span
          ref={titleWrapperRef}
          css={{overflow: 'visible', minWidth: 0}}
          onFocus={measureOverflowIfNeeded}
          onPointerEnter={measureOverflowIfNeeded}
        >
          <AnimatePresence>
            {isVisible ? (
              <animatedView.span
                key={currentTitle}
                css={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                uses={[
                  animate.in({
                    duration: hasAnimatedRef.current
                      ? TITLE_TRANSITION_DURATION_MS
                      : 0,
                    opacity: 0,
                    transform: 'translateY(5px)',
                    easing: 'ease-out',
                  }),
                  animate.out({
                    duration: TITLE_TRANSITION_DURATION_MS,
                    opacity: 0,
                    transform: 'translateY(-10px)',
                    easing: 'ease-in',
                  }),
                ]}
              >
                {currentTitle}
              </animatedView.span>
            ) : null}
          </AnimatePresence>
        </view.span>
      }
    >
      {isOverflowing === true ? currentTitle : null}
    </Tooltip>
  );
}

function ThreadsHeaderBadge({
  conversation,
}: {
  conversation?: Conversation | null;
}) {
  if (!conversation) {
    return null;
  }

  return <ConversationDevBadge conversation={conversation} />;
}

// When history is disabled, the header keeps the legacy (non-threads)
// actions — the More menu and Support Center link — so the experience
// matches the pre-threads assistant.
function ThreadsNoHistoryHeaderActions() {
  const app = useThreadsApp();
  const controller = useThreadsController();
  const entryPoint = useThreadsEntryPoint();
  const viewMode = useThreadsViewMode();

  return (
    <HeaderActionsContent
      showMoreMenu={viewMode === ViewMode.Drawer}
      showSupportCenter={entryPoint === 'dashboard'}
      onRestartConversation={() => {
        controller.ui.requestRestart();
      }}
      onSupportCenterClick={() => {
        app.requestAction({
          action: {
            name: 'navigate',
            data: {
              route: {
                name: 'SUPPORT',
                params: {cad: {connectedAccountId: undefined}},
              },
            },
          },
        });
      }}
    />
  );
}

function ThreadsHeaderActions() {
  const controller = useThreadsController();
  const historyViewModel = useThreadsHistoryViewModel();
  const withHistory = useThreadsWithHistory();
  const analytics = useAnalytics();
  const intl = useIntl();

  if (!withHistory) {
    return <ThreadsNoHistoryHeaderActions />;
  }

  return (
    <>
      <Tooltip
        type="label"
        trigger={
          <Action
            uses={[buttonStyles]}
            aria-label={intl.formatMessage(historyMessages.newConversation)}
            onClick={() => {
              analytics.action(
                'stripe_assistant_header_new_conversation_click',
              );
              controller.ui.startNewConversation();
            }}
          >
            <Icon
              icon={add}
              size="xsmall"
              data-testid="assistant-header-new-conversation"
            />
          </Action>
        }
      >
        <FormattedMessage {...historyMessages.newConversation} />
      </Tooltip>

      <Tooltip
        type="label"
        trigger={
          <Action
            uses={[buttonStyles]}
            aria-label={intl.formatMessage(historyMessages.conversationHistory)}
            onClick={() => {
              analytics.action('stripe_assistant_header_history_click');
              historyViewModel.open();
            }}
          >
            <Icon
              icon={historyIcon}
              size="xsmall"
              data-testid="assistant-header-history"
            />
          </Action>
        }
      >
        <FormattedMessage {...historyMessages.conversationHistory} />
      </Tooltip>
    </>
  );
}

function ThreadsDrawerHeader({
  conversation,
  title,
  height,
  badge,
  headerActions,
  subHeader,
}: Props) {
  const parentMessageBus = useThreadsParentMessageBus();
  const ref = useRef<HTMLDivElement | null>(null);
  const {close} = useThreadsVisibility();
  const {id: conversationId} = useThreadsConversationViewModelState();

  useEffect(() => {
    if (ref.current) {
      sendMinimizedHeight(parentMessageBus, ref.current.offsetHeight);
    }
  }, [parentMessageBus]);

  return (
    <AssistantDrawerHeader
      ref={ref}
      height={height}
      title={<AnimatedTitle key={conversationId} title={title} />}
      badge={badge ?? <ThreadsHeaderBadge conversation={conversation} />}
      headerActions={headerActions ?? <ThreadsHeaderActions />}
      subHeader={
        subHeader ?? <ThreadsActiveTopBar conversation={conversation} />
      }
      hideSparkle
      onClose={close}
    />
  );
}

function ThreadsWidgetHeader({
  conversation,
  title,
  height,
  badge,
  headerActions,
  subHeader,
}: Props) {
  const parentMessageBus = useThreadsParentMessageBus();
  const ref = useRef<HTMLDivElement | null>(null);
  const {visibility, open, close, minimize} = useThreadsVisibility();
  const {id: conversationId} = useThreadsConversationViewModelState();

  useEffect(() => {
    if (ref.current) {
      sendMinimizedHeight(parentMessageBus, ref.current.offsetHeight);
    }
  }, [parentMessageBus]);

  return (
    <AssistantWidgetHeader
      ref={ref}
      height={height}
      title={<AnimatedTitle key={conversationId} title={title} />}
      badge={badge ?? <ThreadsHeaderBadge conversation={conversation} />}
      headerActions={headerActions ?? <ThreadsHeaderActions />}
      subHeader={
        subHeader ?? <ThreadsActiveTopBar conversation={conversation} />
      }
      visibility={visibility}
      hideSparkle
      onOpen={open}
      onMinimize={minimize}
      onClose={close}
    />
  );
}

export function ThreadsConversationScreenHeader(props: Props) {
  const viewMode = useThreadsViewMode();

  if (viewMode === ViewMode.Drawer) {
    return <ThreadsDrawerHeader {...props} />;
  }

  return <ThreadsWidgetHeader {...props} />;
}
